import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Search, Bookmark, Clock, ArrowRight, Zap,
  MapPin, Users, ChevronRight, CheckCircle2, BarChart2,
  Star, Award, Database, AlertTriangle, Bell, UserPlus, X } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [history,    setHistory]    = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [invites,    setInvites]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [accepting,  setAccepting]  = useState(null);
  const [dismissedInvites, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_invites')||'[]'); } catch { return []; }
  });

  const loadData = useCallback(async () => {
    await Promise.all([
      api.get('/leads/history').then(r => setHistory(r.data.history?.slice(0,5)||[])).catch(()=>{}),
      api.get('/leads/saved',{params:{limit:1}}).then(r => setSavedCount(r.data.total||0)).catch(()=>{}),
      api.get('/teams/invites/pending').then(r => setInvites(r.data.invites||[])).catch(()=>{}),
    ]);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const acceptInvite = async (token, inviteId) => {
    setAccepting(inviteId);
    try {
      await api.post('/teams/accept-invitation', { token });
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      const inv = invites.find(i => i.id === inviteId);
      navigate(`/team?joined=${encodeURIComponent(inv?.team_name||'team')}`);
    } catch(err) {
      alert(err.response?.data?.message || 'Failed to accept invitation');
    } finally { setAccepting(null); }
  };

  const dismissInvite = (inviteId) => {
    const updated = [...dismissedInvites, inviteId];
    setDismissed(updated);
    sessionStorage.setItem('dismissed_invites', JSON.stringify(updated));
  };

  const visibleInvites = invites.filter(i => !dismissedInvites.includes(i.id));

  const credits    = user?.search_credits ?? 0;
  const maxCredits = { free:50, individual:500, pro:1000, pro_plus:3000, enterprise:99999 }[user?.plan||'free'] || 50;
  const creditPct  = Math.min(100, (credits / maxCredits) * 100);
  const creditLow  = credits <= 10;

  const STATS = [
    { icon:<Search size={20}/>,   label:'Search',      sub:'Find prospects',                         path:'/search',  color:'var(--accent)',  bg:'var(--accent-bg)'  },
    { icon:<Bookmark size={20}/>, label:'Saved',        sub:`${savedCount.toLocaleString()} leads`,  path:'/saved',   color:'var(--blue)',    bg:'var(--blue-bg)'    },
    { icon:<Users size={20}/>,    label:'Team',         sub: visibleInvites.length > 0 ? `${visibleInvites.length} invite${visibleInvites.length>1?'s':''}` : 'Manage', path:'/team', color:'var(--purple)', bg:'var(--purple-bg)', badge: visibleInvites.length || null },
    { icon:<Clock size={20}/>,    label:'History',      sub:`${history.length} recent`,              path:'/history', color:'var(--green)',   bg:'var(--green-bg)'   },
  ];

  const STEPS = [
    { num:'01', title:'Search a location',    desc:'Enter any city or area to find nearby businesses', done:(user?.total_searches||0)>0 },
    { num:'02', title:'Filter by category',   desc:'Pick business types relevant to your product',     done:false },
    { num:'03', title:'Save qualified leads', desc:'Build your pipeline with the best prospects',       done:savedCount>0 },
    { num:'04', title:'Export & outreach',    desc:'Download CSV and start your outreach campaign',     done:false },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  return (
    <div style={{ maxWidth:1200, margin:'0 auto', padding:'20px 16px' }}>
      <style>{`
        /* ── Dashboard responsive grid ── */
        .dash-quick   { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .dash-stats   { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .dash-bottom  { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media(max-width:900px) {
          .dash-bottom { grid-template-columns:1fr; }
          .dash-stats  { grid-template-columns:repeat(2,1fr); }
        }
        @media(max-width:600px) {
          .dash-quick  { grid-template-columns:repeat(2,1fr); gap:10px; }
          .dash-stats  { grid-template-columns:repeat(2,1fr); gap:10px; }
        }
        @media(max-width:360px) {
          .dash-quick  { grid-template-columns:1fr 1fr; gap:8px; }
        }

        /* ── Quick action cards ── */
        .dash-quick-card {
          cursor:pointer;
          position:relative;
          background:var(--surface);
          border:1px solid var(--border);
          border-radius:12px;
          padding:14px 12px;
          display:flex;
          align-items:center;
          gap:10px;
          transition:box-shadow .15s,border-color .15s;
        }
        .dash-quick-card:hover { box-shadow:0 2px 12px rgba(0,0,0,.08); border-color:var(--accent); }

        /* ── Invite card ── */
        .invite-card {
          background:var(--purple-bg);
          border:1px solid var(--purple);
          border-radius:12px;
          padding:14px 16px;
          display:flex;
          align-items:center;
          gap:12px;
          flex-wrap:wrap;
          animation:fadeInDown .3s ease;
        }
        .invite-actions { display:flex; gap:8px; flex-shrink:0; }

        @media(max-width:600px) {
          .invite-card    { padding:12px; gap:10px; }
          .invite-actions { width:100%; justify-content:flex-end; margin-top:4px; }
          .dash-quick-card { padding:12px 10px; gap:8px; }
        }

        /* ── Stat mini cards ── */
        .stat-mini {
          background:var(--surface);
          border:1px solid var(--border);
          border-radius:10px;
          padding:14px 12px;
          text-align:center;
        }

        @keyframes fadeInDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse      { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
      `}</style>

      {/* ── Welcome ─────────────────────────────────────────────── */}
      <div style={{ marginBottom:18 }}>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(20px,5vw,26px)', fontWeight:800, marginBottom:4 }}>
          Good {greeting}, {user?.first_name}! 👋
        </h1>
        <p style={{ color:'var(--text3)', fontSize:13.5 }}>
          {user?.company}{user?.job_title ? ` · ${user.job_title}` : ''}
        </p>
      </div>

      {/* ── Credits banner ──────────────────────────────────────── */}
      {user?.role !== 'admin' && (
        <div style={{
          background: creditLow ? 'var(--gold-bg)' : 'var(--green-bg)',
          border: `1px solid ${creditLow ? 'var(--gold)' : 'var(--green)'}`,
          borderRadius:12, padding:'12px 16px', marginBottom:16,
          display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
        }}>
          <div style={{ width:36, height:36, borderRadius:9, background: creditLow ? 'var(--gold)' : 'var(--green)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {creditLow ? <AlertTriangle size={17} style={{color:'#fff'}}/> : <Zap size={17} style={{color:'#fff'}}/>}
          </div>
          <div style={{ flex:1, minWidth:160 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
              <span style={{ fontWeight:700, fontSize:13.5 }}>{credits.toLocaleString()} credits remaining</span>
              <span style={{ fontSize:12, color:'var(--text3)', textTransform:'capitalize' }}>{user?.plan||'free'}</span>
            </div>
            <div style={{ height:5, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${creditPct}%`, background: creditLow ? 'var(--gold)' : 'var(--green)', borderRadius:3, transition:'width .4s' }}/>
            </div>
          </div>
          {creditLow && (
            <button className="btn btn-sm" style={{ background:'var(--gold)', color:'#000', border:'none', flexShrink:0 }}
              onClick={() => navigate('/team')}>Upgrade</button>
          )}
        </div>
      )}

      {/* ── Team invitations ────────────────────────────────────── */}
      {visibleInvites.length > 0 && (
        <div style={{ marginBottom:16, display:'flex', flexDirection:'column', gap:10 }}>
          {visibleInvites.map(inv => (
            <div key={inv.id} className="invite-card">
              <div style={{ width:40, height:40, borderRadius:10, background:inv.team_color||'var(--purple)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>👥</div>
              <div style={{ flex:1, minWidth:140 }}>
                <div style={{ fontWeight:700, fontSize:13.5, marginBottom:2, display:'flex', alignItems:'center', gap:6 }}>
                  <Bell size={13} style={{ color:'var(--purple)' }}/> Team Invitation
                </div>
                <div style={{ fontSize:12.5, color:'var(--text2)', lineHeight:1.4 }}>
                  <strong>{inv.invited_by_name}</strong> invited you to join{' '}
                  <strong style={{ color:'var(--purple)' }}>{inv.team_name}</strong> as <strong>{inv.role}</strong>
                  <span style={{ display:'block', fontSize:11.5, color:'var(--text3)', marginTop:2 }}>
                    Expires {new Date(inv.expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                  </span>
                </div>
              </div>
              <div className="invite-actions">
                <button className="btn btn-primary btn-sm" style={{ background:'var(--purple)', borderColor:'var(--purple)' }}
                  disabled={accepting === inv.id} onClick={() => acceptInvite(inv.token, inv.id)}>
                  {accepting === inv.id ? <><div className="spinner spinner-sm"/>&nbsp;Joining…</> : <><UserPlus size={13}/> Accept</>}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/team/accept?token=${inv.token}`)}>View</button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => dismissInvite(inv.id)} style={{ color:'var(--text3)' }}><X size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Quick action cards ───────────────────────────────────── */}
      <div className="dash-quick" style={{ marginBottom:16 }}>
        {STATS.map((s, i) => (
          <div key={i} className="dash-quick-card" onClick={() => navigate(s.path)}>
            <div style={{ position:'relative', flexShrink:0 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', color:s.color }}>
                {s.icon}
              </div>
              {s.badge && (
                <div style={{ position:'absolute', top:-6, right:-6, width:18, height:18, borderRadius:'50%', background:'var(--red)', color:'#fff', fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid var(--surface)', animation:'pulse 1.5s infinite' }}>
                  {s.badge}
                </div>
              )}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13.5, marginBottom:1 }}>{s.label}</div>
              <div style={{ fontSize:11.5, color: s.badge ? 'var(--purple)' : 'var(--text3)', fontWeight: s.badge ? 600 : 400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.sub}</div>
            </div>
            <ChevronRight size={13} style={{ color:'var(--text3)', flexShrink:0 }}/>
          </div>
        ))}
      </div>

      {/* ── Stats row ────────────────────────────────────────────── */}
      <div className="dash-stats" style={{ marginBottom:16 }}>
        {[
          { label:'Searches',   val:(user?.total_searches||0).toLocaleString(),    color:'var(--accent)',  icon:<BarChart2 size={15}/> },
          { label:'Leads Found',val:(user?.total_leads_found||0).toLocaleString(), color:'var(--blue)',    icon:<Database size={15}/> },
          { label:'Saved',      val:savedCount.toLocaleString(),                   color:'var(--purple)',  icon:<Bookmark size={15}/> },
          { label:'Plan',       val:(user?.plan||'free').toUpperCase(),             color:'var(--green)',   icon:<Award size={15}/> },
        ].map(s => (
          <div key={s.label} className="stat-mini">
            <div style={{ color:s.color, marginBottom:5, display:'flex', justifyContent:'center' }}>{s.icon}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'clamp(16px,4vw,20px)', fontWeight:800, color:s.color, marginBottom:2 }}>{s.val}</div>
            <div style={{ fontSize:11, color:'var(--text3)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Bottom: Recent searches + Quick start ───────────────── */}
      <div className="dash-bottom">

        {/* Recent searches */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Searches</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/history')}>View all <ArrowRight size={13}/></button>
          </div>
          {loading
            ? <div style={{ textAlign:'center', padding:24 }}><div className="spinner spinner-md" style={{ margin:'0 auto' }}/></div>
            : history.length === 0
              ? (
                <div style={{ textAlign:'center', padding:'24px 0' }}>
                  <Search size={28} style={{ color:'var(--text3)', marginBottom:10 }}/>
                  <div style={{ fontSize:13.5, color:'var(--text3)', marginBottom:14 }}>No searches yet</div>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/search')}>Start searching</button>
                </div>
              ) : (
                <div>
                  {history.map((h, i) => (
                    <div key={h.id} style={{ padding:'10px 0', borderBottom: i < history.length-1 ? '1px solid var(--border)' : 'none', display:'flex', alignItems:'flex-start', gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:8, background:'var(--accent-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <MapPin size={13} style={{ color:'var(--accent)' }}/>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.location}</div>
                        <div style={{ fontSize:11.5, color:'var(--text3)' }}>{h.results_count} leads · {new Date(h.searched_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
                      </div>
                      <span className="badge badge-accent" style={{ flexShrink:0 }}>{h.radius>=1000?`${h.radius/1000}km`:`${h.radius}m`}</span>
                    </div>
                  ))}
                  <button className="btn btn-ghost btn-full btn-sm" style={{ marginTop:12 }} onClick={() => navigate('/search')}>
                    <Search size={13}/> New Search
                  </button>
                </div>
              )
          }
        </div>

        {/* Quick start */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🚀 Quick Start</div>
            <span style={{ fontSize:12, color:'var(--text3)' }}>{STEPS.filter(s=>s.done).length}/{STEPS.length} done</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', opacity: s.done ? .7 : 1 }}>
                <div style={{ width:30, height:30, borderRadius:8, background: s.done ? 'var(--green-bg)' : 'var(--accent-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {s.done
                    ? <CheckCircle2 size={15} style={{ color:'var(--green)' }}/>
                    : <span style={{ color:'var(--accent)', fontSize:10, fontWeight:800 }}>{s.num}</span>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, textDecoration: s.done ? 'line-through' : 'none', color: s.done ? 'var(--text3)' : 'var(--text)', marginBottom:2 }}>{s.title}</div>
                  <div style={{ fontSize:12, color:'var(--text3)', lineHeight:1.4 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <button className="btn btn-primary btn-full mt-16" onClick={() => navigate('/search')}>
            <Search size={14}/> Search Leads Now
          </button>
        </div>

      </div>
    </div>
  );
}